<template>
    <chart-panel class="chart-container" border>
        <div class="d-flex title-area" style="border-bottom: 1px solid rgb(224, 225, 230);">
            <div class="d-flex">
                <div class="my-3 ml-3" style="font-size: 18px; padding-right: 10px;">{{ data.show === 'district' ? `Chronic Absence Rate` : data.config.exporting.filename }}</div>
                <fe-info-tip
                    color="#495B8F !important"
                    icon="fal fa-info-circle"
                    size="14px"
                    style="padding-right: 8px; align-self: center;"
                    tooltip="Chronic absence is when a student is absent (excused or unexcused) for at least 10% of the days. The overall rate indicates the percentage of students who are chronically absent in a given time period."
                    />
            </div>

            <v-spacer/>

            <v-menu offset-y left >
                <template v-slot:activator="{ on }">
                    <v-btn text icon data-test="ca-rate-chart-extra-menu-items">
                        <v-icon v-on="on" @click="close = false">more_vert</v-icon>
                    </v-btn>
                </template>
                <slot name="menu">
                    <v-card flat style="background: #fff; padding: 10px">
                        <slot name="extra-menu-items" ></slot>
                        <v-btn class="pa-0" text @click="printChart" data-test="chronic-absence-ellipses-pdf">
                            <v-icon class="mr-2" style="color:rgba(0, 0, 0, 0.54)">fa-print</v-icon>
                            Print PDF
                        </v-btn>
                        <br>
                        <v-btn class="pa-0" text @click="exportChart" data-test="chronic-absence-ellipses-image">
                            <v-icon class="mr-2" style="color:rgba(0, 0, 0, 0.54)">fa-download</v-icon>
                            Download Image
                        </v-btn>
                        <br>
                        <v-btn class="pa-0" text @click="exportCSV" data-test="chronic-absence-ellipses-csv">
                            <v-icon class="mr-2" style="color:rgba(0, 0, 0, 0.54)">fa-download</v-icon>
                            Download Spreadsheet
                        </v-btn>
                        <br>
                        <v-btn class="pa-0" text @click="toggleCounts()" data-test="chronic-absence-ellipses-hide-counts">
                            <v-icon class="mr-2" style="color:rgba(0, 0, 0, 0.54)">fa-hashtag</v-icon>
                            {{ toggleItemName }}
                        </v-btn>
                        <br>
                    </v-card>
                </slot>
            </v-menu>
        </div>

        <div class="d-flex field-area">
            <v-spacer/>
            <fe-remote-combo
                v-model="selectedTimePeriod"
                class="mr-3"
                :items="dynamicTimePeriodOptions"
                :clearable="false"
                @input="timePeriodInput"
                data-test="chronic-absence-filter-time"
            />
        </div>

        <div class="chart-area" ref="chartContainer">
            <fe-spinner v-if="!ready" class="spinner"/>

            <data-unavailable v-if="error" data-type="chronic absence"/>

            <no-filter-results
                v-else-if="noFilterResults && ready"
                data-type="chronic absence"
                @clearFilters="$emit('clearAllFilters')"
            />

            <no-school-groups
                v-else-if="noSchoolGroups"
                class="center-no-school-groups-text"
                :canManageSchools="canManageSchools"
                :dataTestBaseString="'ca-rate-chart-'"
            />

            <highcharts
                v-else
                id="caRatetId"
                ref="chronicAbsenceRateChart"
                class="ca-rate-chart"
                :options="chartConfig"
            />
        </div>
    </chart-panel>
</template>

<script>
import ChartPanel from '@/components/charts/ChartPanel'
import * as ChartExportUtil from '../common/util/ChartExports'
import { exportCSV } from '../common/util/ChartExports'
import DataUnavailable from './emptyStateMessaging/DataUnavailable.vue'
import NoFilterResults from './emptyStateMessaging/NoFilterResults.vue'
import NoSchoolGroups from './emptyStateMessaging/NoSchoolGroups.vue'

export default {
    name: 'ChronicAbsenceRateChart',

    components: {
        ChartPanel,
        DataUnavailable,
        NoFilterResults,
        NoSchoolGroups
    },

    props: {
        data: {
            type: Object,
            required: true
        },
        year: {},
        ready: {
            type: Boolean,
            default: false
        },
        error: {
            type: Boolean,
            default: false
        },
        noFilterResults: {
            type: Boolean,
            default: false
        },
        toggle: {
            type: Boolean,
        },
        noSchoolGroups: {
            type: Boolean,
            default: false
        },
        canManageSchools: {
            type: Boolean,
            default: false
        },
        drilldownEnabled: {
            type: Boolean,
            default: false
        },
        phaseTwoFlag: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            selectedTimePeriod: {},
            timePeriodOptions: [{
                name: 'This Year (Monthly)',
                id: '1M',
            }, {
                name: 'Last 2 Years (Monthly)',
                id: '2M',
            }, {
                name: 'Last 5 Years (Monthly)',
                id: '5M',
            },
            {
                name: 'This Year (Annually)',
                id: '1Y',
            },
            {
                name: 'Last 2 Years (Annually)',
                id: '2Y',
            },
            {
                name: 'Last 5 Years (Annually)',
                id: '5Y',
            }],
            toggleEnabled: true,
            viewMode: 'chart',
        }
    },

    computed: {
        chartConfig() {
            const config = this.$_.cloneDeep(this.data.config)
            
            if (this.drilldownEnabled && this.phaseTwoFlag && config?.plotOptions) {
                
                // Make sure necessary structures exist
                config.plotOptions = config.plotOptions || {}
                config.plotOptions.column = config.plotOptions.column || {}
                config.plotOptions.column.point = config.plotOptions.column.point || {}
                config.plotOptions.column.point.events = config.plotOptions.column.point.events || {}
                
                const me = this
                
                config.plotOptions.column.point.events.click = function() {
                    me.$emit('barClick', {
                        category: this.category || '',
                        series: this.series.name || '',
                        value: this.y
                    })
                }
            }
            
            return config
        },

        toggleItemName() {
            return (this.toggle ? 'Hide' : 'Show') + ' Counts on Chart'
        },

        dynamicTimePeriodOptions() {
            if (this.data.show === 'district') {
                return this.timePeriodOptions.filter(option => option.id !== '1Y')
            } else {
                return this.timePeriodOptions.filter(option => option.name.includes('Annual'))
            }
        }
    },

    mounted() {
        this.selectedTimePeriod = this.year
        this.toggleEnabled = this.toggle
    },

    methods: {
        timePeriodInput(period) {
            if (period && period !== this.year) {
                this.$emit('change', 'timePeriod', period)
            }
        },

        async printChart() {
            const node = this.$refs['chronicAbsenceRateChart'].$el
            await ChartExportUtil.downloadAsPDF(node, { landscape: false, delay: 500 })
        },

        exportChart() {
            const fileName = this.data.show === 'district' ? 'Chronic Absence Rate' : this.data.config.exporting.filename
            ChartExportUtil.exportChartFromNode(this.$refs.chartContainer, fileName, this)
        },

        exportCSV() {
            const fileName = this.data.show === 'district' ? 'Chronic Absence Rate' : this.data.config.exporting.filename
            exportCSV(this.$refs['chronicAbsenceRateChart'].chart, fileName)
        },

        toggleCounts() {
            let labelEnabled = this.data.config.plotOptions.column.dataLabels.enabled
            this.$emit('change', 'countToggle', !labelEnabled, true)

            setTimeout(() => {
                this.toggleEnabled = !labelEnabled
            }, 500)
        },
    },
}
</script>

<style scoped lang="scss">
.chart-container {
    background: #fff;
    min-height: 523px;

    .field-area {
        height: 56px;
        padding-top: 10px;
    }

    .chart-area {

        .ca-rate-chart {
            height: 100%;

            ::v-deep * {
                font-family: 'HyperlegibleSans-Regular', Hyperlegible Sans, 'Roboto', sans-serif !important;
            }

            ::v-deep .highcharts-container {
                width: 100% !important;
            }

            &-menu {
                &-btn {
                    position: relative;
                    top: -400px;
                    z-index: 1;
                    float: right;
                    color: #7E8494 !important;
                }
            }
        }
    }
}

.spinner {
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    line-height: 250px !important;
    z-index: 1000;
}

.center-no-school-groups-text {
    width: 100%;
    margin: 0;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center !important;
}
</style>
